/**
 * PendingAIOfferInbox — pupil-side inbox for instructor-pushed
 * "Offer AI Assessment" requests (Track A, Phase 4 / Phase C).
 *
 * Operator brief (Feb 13, 2026):
 *   "Pupil can see pending AI offers. Pupil can accept and reach
 *    Stripe checkout. Pupil can decline offer. Cancelled / expired
 *    / declined offers display calmly."
 *
 * Mount target: top of /learn, below Track-C onboarding/banner.
 *
 * What it shows:
 *   • Active actionable offers (status === "pending")
 *     → big calm card per offer with retail price, assessment type,
 *       Accept (→ /ai-offer-accept?id=...) + Decline buttons.
 *   • Already-paid offers awaiting upload (status === "paid")
 *     → quiet reminder strip with deep-link to /training?intent=...
 *       &ai_offer_id=...
 *   • Recent terminal offers (declined / cancelled / expired) older
 *     than the actionable ones are hidden from this card by default.
 *
 * What it deliberately does NOT do:
 *   • No telemetry / analytics.
 *   • No subscription upsell.
 *   • No instructor-facing surface (Phase D).
 *   • No AI-completion confirmation surface (Phase E).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, X, Upload, Clock,
} from "lucide-react";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;


function formatPrice(pence) {
  const n = (pence || 0) / 100;
  return `£${n.toFixed(2)}`;
}


export default function PendingAIOfferInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [declining, setDeclining] = useState(null);

  const load = async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      // Media quarantine: don't poll the quarantined endpoint.
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/ai-offers/pupil/list`, {
        withCredentials: true,
      });
      setItems(r.data?.items || []);
    } catch {
      // Calmly fail — pupils without offers shouldn't see anything,
      // and a transient outage is not worth a toast on the home page.
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decline = async (offer) => {
    setDeclining(offer.id);
    try {
      await axios.post(
        `${API}/api/ai-offers/${offer.id}/decline`,
        {},
        { withCredentials: true },
      );
      toast.success("Offer declined.");
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not decline. Please try again.",
      );
    } finally {
      setDeclining(null);
    }
  };

  if (loading) return null;

  // Media quarantine — silent. No CTA into /ai-offer-accept.
  if (!MEDIA_FEATURE_ENABLED) return null;

  // Show only the offer states the pupil can act on or that signal a
  // pending obligation. Terminal ones (declined/cancelled/expired)
  // stay out of the home card — they're not actionable.
  const actionable = items.filter(
    (o) => o.status === "pending" || o.status === "paid"
            || o.status === "accepted",
  );
  if (actionable.length === 0) return null;

  return (
    <section
      data-testid="pending-ai-offer-inbox"
      className="rounded-2xl border border-indigo-700/40 bg-gradient-to-br from-indigo-900/20 to-slate-900/30 p-4 sm:p-5 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-300" />
        <h3
          data-testid="pending-ai-offer-inbox-heading"
          className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300"
        >
          AI assessment offers
        </h3>
      </div>

      <div className="space-y-2.5">
        {actionable.map((o) => (
          <OfferRow
            key={o.id}
            offer={o}
            onDecline={() => decline(o)}
            declining={declining === o.id}
          />
        ))}
      </div>
    </section>
  );
}


function OfferRow({ offer, onDecline, declining }) {
  if (offer.status === "paid") {
    return (
      <Link
        to={`/training?intent=${encodeURIComponent(offer.review_intent)}&ai_offer_id=${offer.id}`}
        data-testid={`pending-ai-offer-${offer.id}-record`}
        className="block rounded-xl border border-emerald-700/40 bg-emerald-900/15 p-3 hover:bg-emerald-900/25 transition"
      >
        <div className="flex items-start gap-2.5">
          <Upload className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[13px] font-extrabold text-emerald-100">
              Record your {offer.review_intent_label}
            </p>
            <p className="text-[11.5px] text-emerald-200/80 mt-0.5 leading-snug">
              Paid — record your drive when you're ready. We'll guide you.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-emerald-300 mt-1 flex-shrink-0" />
        </div>
      </Link>
    );
  }

  if (offer.status === "accepted") {
    return (
      <Link
        to={`/ai-offer-accept?offer_id=${offer.id}`}
        data-testid={`pending-ai-offer-${offer.id}-resume`}
        className="block rounded-xl border border-amber-700/40 bg-amber-900/15 p-3 hover:bg-amber-900/25 transition"
      >
        <div className="flex items-start gap-2.5">
          <Clock className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[13px] font-extrabold text-amber-100">
              {offer.review_intent_label} — payment incomplete
            </p>
            <p className="text-[11.5px] text-amber-200/80 mt-0.5 leading-snug">
              Tap to finish payment ({formatPrice(offer.retail_price_pence)}).
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-amber-300 mt-1 flex-shrink-0" />
        </div>
      </Link>
    );
  }

  // status === "pending"
  return (
    <div
      data-testid={`pending-ai-offer-${offer.id}`}
      className="rounded-xl border border-indigo-700/40 bg-slate-900/40 p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
            From {offer.instructor_name}
          </p>
          <p
            data-testid={`pending-ai-offer-${offer.id}-label`}
            className="text-[14px] font-extrabold text-white mt-0.5"
          >
            {offer.review_intent_label}
          </p>
          <p
            data-testid={`pending-ai-offer-${offer.id}-price`}
            className="text-[12px] text-slate-300 mt-1"
          >
            <span className="font-extrabold text-white">
              {formatPrice(offer.retail_price_pence)}
            </span>
            {" "}— one-off AI assessment
          </p>
          {offer.note ? (
            <p className="text-[11.5px] text-slate-400 mt-1.5 italic">
              "{offer.note}"
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex gap-2 mt-2.5">
        <Link
          to={`/ai-offer-accept?offer_id=${offer.id}`}
          data-testid={`pending-ai-offer-${offer.id}-accept`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-extrabold py-2 active:scale-[0.99] transition"
        >
          Accept <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <button
          type="button"
          data-testid={`pending-ai-offer-${offer.id}-decline`}
          onClick={onDecline}
          disabled={declining}
          className="rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-200 text-sm font-extrabold px-3 py-2 border border-slate-700 active:scale-[0.99] transition flex items-center gap-1"
        >
          <X className="w-3.5 h-3.5" />
          {declining ? "…" : "Decline"}
        </button>
      </div>
    </div>
  );
}
